import React, { useEffect, useRef, useState, useMemo } from "react";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";
import { SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { Chip } from "../../../../assets/components/Chip";
import { useFixedLayout } from "../../../../hooks/useFixedLayout";
import useImageListStore from "../../../../stores/imageList";
import Cafe from "../../../../assets/image/bgExImage/cafe.png";
import Clothes from "../../../../assets/image/bgExImage/clothes.png";
import Hair from "../../../../assets/image/bgExImage/hair.png";
import Meat from "../../../../assets/image/bgExImage/meat.png";
import BgPrompts from "../../../ManualMarketing/ManualMarketingGallery/detailComponents/bgPrompts";
import vintage from "../../../../assets/image/filter/0.jpg";
import two_d from "../../../../assets/image/filter/2.jpg";
import refresh from "../../../../assets/image/filter/3.jpg";

const STYLE_OPTIONS = [
  "원본 사용",
  "배경 AI 변경",
  "이미지 필터",
  "AI 모델 합성",
];

const defaultAiModel = {
  enabled: false,
  modelType: "",
  label: "",
  prompt: "",
  refImagePath: "",
};

const AI_MODEL_DESIGN_ID = "5";

const deriveAiModelKey = (img) => {
  if (!img) return "";
  const candidate =
    img.image_id ??
    img.id ??
    img.imageId ??
    img.key ??
    img.path ??
    img.url;
  return candidate != null ? String(candidate) : "";
};

const normalizeAiModel = (model) => {
  const merged = { ...defaultAiModel, ...(model || {}) };
  merged.enabled = Boolean(merged.enabled && merged.modelType);
  return merged;
};

// 예시 이미지 + 프롬프트 매핑
const bgOptions = [
  { key: "cafe", label: "카페", image: Cafe },
  { key: "clothes", label: "의류 매장", image: Clothes },
  { key: "hair", label: "헤어샵", image: Hair },
  { key: "meat", label: "고깃집", image: Meat },
];

// 예시 이미지 + 필터 인덱스 매핑
const fiOptions = [
  { key: 0, label: "레트로", image: vintage },
  { key: 2, label: "2D", image: two_d },
  { key: 3, label: "상쾌", image: refresh },
];

function mapStyle(aiStyle) {
  if (!aiStyle) return null;
  const codeMap = {
    "1": "원본 사용",
    "3": "배경 AI 변경",
    "4": "이미지 필터",
  };

  if (codeMap[aiStyle]) return codeMap[aiStyle];
  if (STYLE_OPTIONS.includes(aiStyle)) return aiStyle;
  return null;
}

export default function CameraStyleOptionSheet({
  isOpen,
  onClose,
  value,
  aiStyle,
  onConfirm,
  image,
  height = "h-[70%]",
  aiModel = defaultAiModel,
  storeBusinessNumber,
}) {
  const { headerRef, bottomSpace } = useFixedLayout(isOpen);
  const { getImageList } = useImageListStore();
  const recommendedStyle = useMemo(() => mapStyle(aiStyle), [aiStyle]);

  const [selectedStyle, setSelectedStyle] = useState("원본 사용");
  const [bgAiOption, setBgAiOption] = useState(null);
  const [filterOption, setFilterOption] = useState(null);
  const [selectedAiModelKey, setSelectedAiModelKey] = useState(
    aiModel?.modelType || null
  );

  const imageManualList = useMemo(() => {
    if (!storeBusinessNumber) return [];
    const list = getImageList?.(storeBusinessNumber);
    return Array.isArray(list) ? list : [];
  }, [storeBusinessNumber, getImageList]);

  const aiModelOptions = useMemo(() => {
    // 프로젝트 내에 저장된 AI 모델 이미지 파일들 사용
    const staticAiModels = [
      {
        key: "ai_model_1",
        label: "AI 모델 1",
        prompt: "A natural-looking Asian female model with a bright, healthy smile, clear glowing skin, and long dark hair. She has a fresh and pure appearance, suitable for skincare and lifestyle advertising. Soft natural daylight, gentle green foliage background, shallow depth of field. The model is posed naturally, holding or interacting with the product in a realistic and harmonious way. Photorealistic, high resolution, professional commercial photography, clean and elegant composition.",
        path: "/assets/template/aiModel/aiModel_image_1.png",
      },
      {
        key: "ai_model_2", 
        label: "AI 모델 2",
        prompt: "A healthy and energetic Asian female model with a bright, confident smile. She has a fit and athletic body, natural makeup, and long flowing hair. Sporty and active lifestyle appearance, suitable for fitness and wellness advertising. Outdoor tennis court environment, natural daylight, clean and fresh atmosphere. The model interacts naturally with the product in a realistic and balanced pose. Photorealistic, high resolution, professional commercial photography.",
        path: "/assets/template/aiModel/aiModel_image_2.png",
      },
      {
        key: "ai_model_3",
        label: "AI 모델 3", 
        prompt: "A friendly Asian male barista with a warm smile and neat appearance. He is wearing a casual café uniform with an apron. Cozy coffee shop interior, wooden counter, warm ambient lighting. Natural and welcoming posture, interacting realistically with the product. Lifestyle food and beverage advertising style. Photorealistic, high resolution, professional commercial photography.",
        path: "/assets/template/aiModel/aiModel_image_3.png",
      },
      {
        key: "ai_model_4",
        label: "AI 모델 4",
        prompt: "A cheerful young Asian woman with a bright, joyful smile, enjoying Korean food. She has a cute and natural appearance with short wavy hair, wearing a simple and neat white blouse. She is holding chopsticks and happily eating spicy tteokbokki (or red spicy stew) with steam rising from the dish. Cozy home kitchen setting with blue tiled wall in the background, warm and inviting atmosphere. Table full of colorful Korean side dishes (banchan), grilled meat, fresh greens, and various small bowls. Lifestyle food advertising style, appetizing and mouth-watering composition. Photorealistic, high resolution, professional commercial food photography, natural lighting, vibrant colors.",
        path: "/assets/template/aiModel/aiModel_image_4.png",
      },
      {
        key: "ai_model_5",
        label: "AI 모델 5",
        prompt: "This high-quality commercial image combines realistic photography and flat illustration elements. At the center is a Korean woman in her early 20s, photographed from the waist up. She has straight hair with bangs and wears a simple pastel yellow top. Her eyes are wide open, her lips slightly parted, and her expression is a mixture of surprise and curiosity. One hand is placed near her mouth, as if responding to the product. Before her, the product is placed on a clean table, brightly lit and sharply focused. Surrounding the product are a rich array of colorful illustration elements, including stylish leaves, vegetables, flowers, and vibrant organic shapes. The illustration has a hand-drawn, flat feel, featuring bold outlines and vibrant colors. It seamlessly overlaps the actual product, leaving the model's face unobscured. The deep green background serves as a clean canvas on which the illustration elements can unfold. The lighting on the model and product is soft and even, maintaining a natural photographic feel, while the illustration graphics add a whimsical and vibrant contrast. The overall style is a premium lifestyle advertisement with a modern composition that harmoniously combines realism and illustration.",
        path: "/assets/template/aiModel/aiModel_image_5.png",
      },
      {
        key: "ai_model_6",
        label: "AI 모델 6",
        prompt: "A high-quality commercial photo featuring a Korean woman in her late 20s, positioned slightly to the left of the frame. She is leaning forward with her arms gently resting on the table edge, chin slightly lowered, creating a soft and intimate pose. Her expression is warm and playful with a subtle smile, eyes looking directly toward the camera, conveying approachability and curiosity. She wears a simple, casual light-gray top with a clean neckline, emphasizing a natural lifestyle look. Her hair is styled in a loose, slightly messy bun with soft strands framing her face, enhancing a relaxed and cozy mood. Makeup is minimal and natural, highlighting clear skin and gentle facial features. In front of her on the table is a neatly plated food item, placed slightly to the right, acting as a visual focal point without overpowering the model. A small dipping sauce or side element sits nearby, adding detail and realism. The camera angle is a medium close-up shot at eye level, with a shallow depth of field. The background is softly blurred, featuring warm indoor lighting and neutral tones, creating a calm, home-style atmosphere suitable for lifestyle food advertising. Soft, diffused light illuminates the model’s face and the food evenly, producing a premium yet approachable commercial aesthetic.",
        path: "/assets/template/aiModel/aiModel_image_6.png",
      },
      {
        key: "ai_model_7",
        label: "AI 모델 7",
        prompt: "A high-impact commercial image with a deep navy-blue background, featuring a dynamic abstract tiger formed from flowing paint and liquid energy, blending orange and blue tones behind the model. A Korean male athlete in his early 30s stands confidently on the left, with a sharp jawline, short dark hair, and a calm yet powerful expression, wearing a modern dark jacket and holding the collar slightly with one hand. The tiger effect appears to emerge from behind and around the model, symbolizing strength, speed, and dominance, with dramatic motion and premium visual intensity. On the right side, a sleek product package is cleanly positioned, well-lit and sharply focused, balanced against the dynamic background, in a premium global advertising style.",
        path: "/assets/template/aiModel/aiModel_image_7.png",
      },
      {
        key: "ai_model_8",
        label: "AI 모델 8",
        prompt: "A high-quality commercial photo of three Korean adults in their late 20s to early 30s sitting together at an outdoor table. At the center, a cheerful Korean woman with long dark hair smiles brightly toward the camera, wearing a casual light-blue shirt, creating a friendly and approachable mood. The group is naturally interacting, holding drinks and reacting to the food on the table, expressing enjoyment and social connection. The background is a lush garden-style outdoor setting with natural daylight, softly blurred, while a variety of food items are placed across the table as generic product placeholders.",
        path: "/assets/template/aiModel/aiModel_image_8.png",
      },
      {
        key: "ai_model_9",
        label: "AI 모델 9",
        prompt: "This is a realistic advertisement photo, tilted slightly from the front and side. A Korean woman in her late 20s is leaning against a table, her elbows down and her hands resting lightly on her cheeks. Her lips are slightly parted, her expression a mixture of surprise and curiosity, as she gazes intently at the product. She wears a simple wristwatch on one wrist and has minimal, natural makeup. Her black hair is neatly tied back in a low ponytail, with a few strands framing her face. A clean tabletop displays the product neatly before her eyes. The background is a modern studio or kitchen, with muted colors, softly visible equipment, and diffused lighting that creates a natural advertising photoshoot atmosphere.",
        path: "/assets/template/aiModel/aiModel_image_9.png",
      },
      {
        key: "ai_model_10",
        label: "AI 모델 10",
        prompt: "This high-quality commercial portrait depicts a Korean man, presumably in his late 30s to early 40s (probable model: Shin Dong-yup). The photograph was taken against a dark, textured, dark gray studio background. His neatly styled short black hair is neatly brushed to the side, and he exudes a confident yet playful, sly smile and sharp gaze directed at the camera. The shot is taken from a frontal and side angle, with his face turned towards the camera. The photograph features a classic yet approachable look, paired with a subtly patterned long-sleeved shirt and red suspenders. He is seated or slightly leaning forward, holding a fork (or chopsticks) in one hand close to his face, as if about to savor the food, while supporting a bowl of food with the other. The lighting is dramatic yet restrained, creating soft highlights on his face and food, while subtle shadows add depth, creating a luxurious and powerful food advertising atmosphere.",
        path: "/assets/template/aiModel/aiModel_image_10.png",
      },
      {
        key: "ai_model_11",
        label: "AI 모델 11",
        prompt: "This photo depicts a Korean woman in her early 20s and a Korean man in his late 30s seated side by side at a red table. The woman on the left has long, wavy hair, bangs down, clear, fair skin, and subtle makeup that emphasizes her large eyes and glossy lips. She has a mole under her right eye. She wears a long-sleeved white knit top that reveals her shoulders, holds a can of soda close to her face, and smiles slightly toward the camera. The man on the right has naturally styled short black hair, clear skin, and a calm, relaxed expression. He wears a light gray hoodie, resting his chin on one hand and looking directly at the camera, leaning slightly forward in a relaxed posture. Between them is a bright red table neatly arranged with merchandise. The background is a warm and cozy interior with soft lighting, delicate decorations, and a shallow depth of field, creating an intimate lifestyle advertising atmosphere.",
        path: "/assets/template/aiModel/aiModel_image_11.png",
      },
      {
        key: "ai_model_12",
        label: "AI 모델 12",
        prompt: "The setting is a quiet, modern restaurant or lounge. A middle-aged Korean man with gray hair and glasses sits upright at a table, looking straight ahead. He wears a textured gray blazer over a dark shirt, holding a glass in one hand, and his expression is calm and thoughtful. His gaze is not directly at the camera, but slightly upward and forward, conveying a sense of deep thought and subtle tension. In the foreground, on the right side of the frame, a young Korean man, slightly out of focus, sits with his back to the camera. Only part of his face is visible in profile, and his gaze is directed toward the table, suggesting hesitation or inner conflict. He wears a dark suit jacket and has neatly trimmed short hair, highlighting the contrast between generations. On the table, products are neatly arranged between the two men, under soft lighting, preventing them from drawing too much attention. The warm, soft lighting, soft shadows, and shallow depth of field create an intimate, cinematic atmosphere with emotional weight. The background features subtle architectural textures and soft window light, further enhancing the understated, dramatic atmosphere.",
        path: "/assets/template/aiModel/aiModel_image_12.png",
      },
    ];

    return staticAiModels;
  }, []);

  const firstAiModelKey = aiModelOptions[0]?.key || null;

  const normalizedAiModel = useMemo(() => normalizeAiModel(aiModel), [aiModel]);
  const selectedAiModel = useMemo(
    () => aiModelOptions.find((option) => option.key === selectedAiModelKey) || null,
    [aiModelOptions, selectedAiModelKey]
  );

  useEffect(() => {
    if (!isOpen) return;

    // 스타일 초기값
    if (value && STYLE_OPTIONS.includes(value)) {
      setSelectedStyle(value);
    } else if (recommendedStyle) {
      setSelectedStyle(recommendedStyle);
    } else {
      setSelectedStyle("원본 사용");
    }

    setFilterOption(null);
    setSelectedAiModelKey(
      value === "AI 모델 합성" && normalizedAiModel.modelType
        ? normalizedAiModel.modelType
        : value === "AI 모델 합성"
        ? firstAiModelKey
        : null
    );
  }, [
    isOpen,
    value,
    recommendedStyle,
    normalizedAiModel.modelType,
    firstAiModelKey,
  ]);

  // 칩 클릭 핸들러
  const handleSelectStyle = (option) => {
    setSelectedStyle(option);

    if (option !== "배경 AI 변경") {
      setBgAiOption(null);
    }
    if (option !== "이미지 필터") {
      setFilterOption(null);
    }
    if (option !== "AI 모델 합성") {
      setSelectedAiModelKey(null);
    } else if (!selectedAiModelKey && firstAiModelKey) {
      setSelectedAiModelKey(firstAiModelKey);
    }
  };

  // 배경 AI 변경에서 이미지 선택
  const handleBgOptionClick = (key) => {
    setSelectedStyle("배경 AI 변경");
    setBgAiOption(key);
  };

  // 이미지 필터에서 이미지 선택
  const handleFilterClick = (key) => {
    setSelectedStyle("이미지 필터");
    setFilterOption(key);
  };

  const handleAiModelClick = (key) => {
    setSelectedStyle("AI 모델 합성");
    setSelectedAiModelKey(key);
  };

  const buildAiModelPayload = () => {
    if (selectedStyle !== "AI 모델 합성") {
      return { ...defaultAiModel };
    }
    const selection = aiModelOptions.find((item) => item.key === selectedAiModelKey);
    if (!selection) {
      return normalizeAiModel(normalizedAiModel.enabled ? normalizedAiModel : defaultAiModel);
    }
    return {
      enabled: true,
      modelType: selection.key,
      label: selection.label,
      prompt: selection.prompt,
      refImagePath: selection.path,
    };
  };

  const handleSave = () => {
    if (!selectedStyle) return;

    let bg_prompt = null;
    let filter = null;

    if (selectedStyle === "배경 AI 변경" && bgAiOption) {
      bg_prompt = BgPrompts[bgAiOption] ?? null;
    }
    if (selectedStyle === "이미지 필터" && filterOption !== null) {
      filter = filterOption;
    }

    if (selectedStyle === "AI 모델 합성" && !selectedAiModelKey) {
      alert("사용할 AI 모델을 선택해주세요.");
      return;
    }

    if (selectedStyle === "AI 모델 합성" && aiModelOptions.length === 0) {
      alert("AI 모델 썸네일을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    const nextAiModel = buildAiModelPayload();

    onConfirm && onConfirm(selectedStyle, bg_prompt, filter, nextAiModel);
    onClose && onClose();
  };

  const handleClose = () => {
    onClose && onClose();
  };

  // 칩 영역: 자동 포커스용 ref
  const containerRef = useRef(null);
  const chipRefs = useRef({}); // key별 chip DOM 저장

  useEffect(() => {
    if (!isOpen || !containerRef.current || !selectedStyle) return;
    const container = containerRef.current;
    const targetEl = chipRefs.current[selectedStyle];
    if (!targetEl) return;

    const chipLeft = targetEl.offsetLeft;
    const chipWidth = targetEl.offsetWidth;
    const containerWidth = container.clientWidth;

    // 칩이 대략 가운데 오도록
    const scrollLeft = Math.max(
      chipLeft - (containerWidth - chipWidth) / 2,
      0
    );

    container.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }, [isOpen, selectedStyle]);

  // 선택된 chip에 따라 아래 내용 바꾸기
  const renderStyleContent = () => {
    if (selectedStyle === "원본 사용") {
      const isActive = selectedStyle === "원본 사용";
      return (
        <div className="relative w-full flex items-center justify-center bg-surface-light-subtle rounded-[16px]">
          {/* 상단 중앙 칩 */}
          <div className="absolute top-[16px] left-1/2 -translate-x-1/2 z-10">
            <Chip chip="e" label="원본 예시" />
          </div>

          <img
            src={image}
            alt="원본 사용 예시"
            className={"w-full h-[480px] object-contain rounded-[12px] border-2 " +
              (isActive ? "border-border-primary" : "border-transparent")}
          />
        </div>
      );
    }

    if (selectedStyle === "배경 AI 변경") {
      return (
        <div className="grid grid-cols-2 gap-[8px]">
          {bgOptions.map((option) => {
            const isSelected = bgAiOption === option.key;

            return (
              <div key={option.key} className="relative flex flex-col items-center w-full">
                <button
                  type="button"
                  onClick={() => handleBgOptionClick(option.key)}
                  className={
                    "relative w-full aspect-square rounded-[12px] overflow-hidden border-[2px] " +
                    (isSelected ? "border-border-primary" : "border-transparent")
                  }
                >
                  <img
                    src={option.image}
                    alt={option.label}
                    className="w-full h-full object-cover"
                  />

                  {/* 선택된 게 있고, 이 카드는 선택 안 됐으면 흰 레이어 */}
                  {bgAiOption && !isSelected && (
                    <div className="absolute inset-0 bg-surface-white-dimmed_80 pointer-events-none" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      );
    }

    if (selectedStyle === "이미지 필터") {
      return (
        <div className="grid grid-cols-2 gap-[8px]">
          {fiOptions.map((option) => {
            const isSelected = filterOption === option.key;

            return (
              <div key={option.key} className="relative flex flex-col items-center w-full">
                <button
                  type="button"
                  onClick={() => handleFilterClick(option.key)}
                  className={
                    "relative w-full aspect-square rounded-[12px] overflow-hidden border-[2px] " +
                    (isSelected ? "border-border-primary" : "border-transparent")
                  }
                >
                  <img
                    src={option.image}
                    alt={option.label}
                    className="w-full h-full object-cover"
                  />

                  {/* 선택된 게 있고, 이 카드는 선택 안 됐으면 흰 레이어 */}
                  {filterOption !== null && !isSelected && (
                    <div className="absolute inset-0 bg-surface-white-dimmed_80 pointer-events-none" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      );
    }

    if (selectedStyle === "AI 모델 합성") {
      if (aiModelOptions.length === 0) {
        return (
          <div className="w-full min-h-[200px] rounded-[16px] border border-border-subtle flex items-center justify-center text-b1 text-text-subtler text-center px-[16px]">
            사용할 수 있는 AI 모델 썸네일이 없습니다. 매장 정보를 다시 불러온 뒤 시도해주세요.
          </div>
        );
      }

      return (
        <div className="grid grid-cols-2 gap-[8px]">
          {aiModelOptions.map((option) => {
            const isSelected = selectedAiModelKey === option.key;
            return (
              <div key={option.key} className="relative flex flex-col items-center w-full">
                <button
                  type="button"
                  onClick={() => handleAiModelClick(option.key)}
                  className={`relative w-full aspect-square rounded-[12px] overflow-hidden border-[2px] transition ${
                    isSelected ? "border-border-primary" : "border-transparent"
                  }`}
                >
                  {option.path ? (
                    <img
                      src={option.path}
                      alt={option.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-surface-light-subtle flex items-center justify-center text-c1 text-text-subtler">
                      이미지 없음
                    </div>
                  )}

                  {selectedAiModelKey && !isSelected && (
                    <div className="absolute inset-0 bg-surface-white-dimmed_80 pointer-events-none" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      );
    }


    return null;
  };

  return (
    <BottomWithButton
      height={height}
      isOpen={isOpen}
      onClose={handleClose}
      title="이미지 스타일"
      subtitle="어떤 스타일로 변경하고 싶나요? (이미지 선택 1개)"
      onSubmit={handleSave}
      renderButton={({ onClick }) => (
        <SplitRowQuarter
          leftText="닫기"
          onLeft={handleClose}
          rightType="fill"
          rightText="저장"
          onRight={onClick}
        />
      )}
    >
      <div className="flex flex-col h-full">
        <div ref={headerRef} className="shrink-0 bg-surface-default">
          {/* Chip 영역 */}
          <div
            ref={containerRef}
            className="flex overflow-x-auto space-x-[8px] pb-[16px] no-scrollbar cursor-grab active:cursor-grabbing"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {STYLE_OPTIONS.map((option) => {
              const isSelected = selectedStyle === option;
              const isReco = recommendedStyle === option;

              return (
                <div key={option} className="flex-shrink-0"
                  ref={(el) => {
                    if (el) chipRefs.current[option] = el;
                  }}
                >
                  <Chip
                    onClick={() => handleSelectStyle(option)}
                    chip="a"
                    status={isSelected ? "selected" : "default"}
                    label={option}
                    allowBadge
                    badgeLabel={isReco ? "AI 추천" : undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Chip에 따라 바뀌는 내용 영역 */}
        <div className="flex-1 overflow-y-auto no-scrollbar" style={{ paddingBottom: bottomSpace }}>{renderStyleContent()}</div>
      </div>
    </BottomWithButton>
  );
}
